import React from 'react';
import { OperationMS } from '../types';
import { CommandeAnalyse, CritereKey, TechNotes } from '../domain/analyse/types';
import {
  CRITERES_TECHNIQUES,
  CRITERES_MEMO,
  computeNoteGlobale,
  formatNoteGlobale,
  NotationErrors,
} from '../domain/analyse/notationTechnique';
import { isOffreMasquee, isExpire, isRefuse, isAccepte, isPending } from '../domain/analyse/statutOffre';
import { getCandidatureDate } from '../domain/analyse/historique';
import { formatDateHeure } from '../domain/analyse/dates';

// Textarea auto-extensible : la hauteur suit le contenu (scrollHeight) -> pas de scroll
// interne ni de poignee de resize, la zone s'adapte directement a la longueur du texte.
const AutoGrowTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [props.value]);
  return <textarea {...props} ref={ref} rows={1} />;
};

// Grille de notation technique : une colonne par offre, 3 criteres (organisation/moyens/social).
// Toute la logique (bareme, note globale, statuts) vient du domaine. Editabilite par un seul
// predicat applique au commentaire ET a la note (corrige le verrouillage incoherent du legacy).
interface OperationAnalyseTechniqueProps {
  operation: OperationMS;
  techNotes: TechNotes;
  setNote: (commandeId: number | string, critere: CritereKey, patch: { note?: number; commentaire?: string }) => void;
  errors: NotationErrors;
  viewOnly: boolean;
  iaEnCours?: boolean;
}

const OperationAnalyseTechnique: React.FC<OperationAnalyseTechniqueProps> = ({
  operation,
  techNotes,
  setNote,
  errors,
  viewOnly,
  iaEnCours = false,
}) => {
  const commandes = (operation.commandes ?? []) as CommandeAnalyse[];

  return (
    <>
      <div className="opms__critere-memo">
        <svg
          className="opms__critere-memo-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#595959"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="8" x2="12" y2="8" />
          <line x1="12" y1="11" x2="12" y2="16" />
        </svg>
        <div className="opms__critere-memo-content">
          <div className="opms__critere-memo-preview">{CRITERES_MEMO}</div>
          <div className="opms__tooltip">{CRITERES_MEMO}</div>
        </div>
      </div>

      {commandes.length === 0 ? (
        <p className="opms__empty">Aucun prestataire n'est disponible pour cette opération.</p>
      ) : (
        <div className="opms__presta-grid">
          {commandes.map((commande, idx) => {
        const key = String(commande.id);
        const idStatus = commande.id_status;
        const hidden = isOffreMasquee(idStatus);
        const fieldDisabled = viewOnly || hidden || isPending(idStatus) || isAccepte(idStatus);
        const organisme = commande.contrat?.entite?.nom || `Prestataire ${idx + 1}`;
        const candidatureDate = formatDateHeure(getCandidatureDate(operation.historique, commande.id));
        const noteEntries = techNotes[key] || {};
        // Reponse technique : precedence commande.reponse -> commande.reponse_technique ->
        // operation.reponse_technique (comme le legacy) ; "Offre non reçue" si vide.
        const reponseTechnique =
          commande.reponse || commande.reponse_technique || operation.reponse_technique;

        return (
          <div
            key={key}
            className={`opms__presta-col${hidden ? ' opms__presta-col--disabled' : ''}`}
          >
            {isExpire(idStatus) && <div className="opms__presta-badge">Expiré</div>}
            {isRefuse(idStatus) && <div className="opms__presta-badge">Refusé</div>}
            {isPending(idStatus) && <div className="opms__presta-badge opms__presta-badge--ok">Signature en cours</div>}
            {isAccepte(idStatus) && <div className="opms__presta-badge opms__presta-badge--ok">Accepté</div>}

            <div className="opms__presta-header">
              <h3>{organisme}</h3>
              <span>{hidden ? '-' : candidatureDate ? `Offre déposée le ${candidatureDate}` : 'Offre déposée'}</span>
            </div>

            <div className="opms__field">
              <label>Réponse technique au besoin :</label>
              <div className="opms__readonly-text">
                {hidden ? '-' : reponseTechnique || 'Offre non reçue'}
              </div>
            </div>

            <hr className="opms__sep" />

            {CRITERES_TECHNIQUES.map((critere) => {
              const entry = noteEntries[critere.key];
              const fieldErr = errors[key]?.[critere.key];
              return (
                <div key={critere.key} className="opms__field">
                  <span className="opms__field-label">{critere.label}</span>
                  <div className="opms__readonly-text">
                    {hidden ? '-' : entry?.description || critere.description}
                  </div>

                  <label>
                    Votre commentaire<span className="opms__required">*</span>
                  </label>
                  {iaEnCours ? (
                    <div className="opms-sk-comment" aria-hidden="true">
                      <span className="opms-sk opms-sk-line" />
                      <span className="opms-sk opms-sk-line" />
                      <span className="opms-sk opms-sk-line opms-sk-line--short" />
                    </div>
                  ) : (
                    <AutoGrowTextarea
                      id={`${key}-${critere.key}-commentaire`}
                      placeholder="Votre commentaire"
                      value={hidden ? '-' : entry?.commentaire || ''}
                      onChange={(e) => setNote(commande.id ?? key, critere.key, { commentaire: e.target.value })}
                      className={`opms__input${fieldErr?.commentaire ? ' opms__input--error' : ''}`}
                      disabled={fieldDisabled}
                    />
                  )}

                  <div className="opms__note-row">
                    <label>
                      Note<span className="opms__required">*</span> :
                    </label>
                    {hidden ? (
                      <div className="opms__input opms__note-select opms__input--inactif">-</div>
                    ) : iaEnCours ? (
                      <span className="opms-sk opms-sk-note opms__note-select" aria-hidden="true" />
                    ) : (
                      <select
                        id={`${key}-${critere.key}-note`}
                        value={String(entry?.note ?? critere.options[0].value)}
                        onChange={(e) => setNote(commande.id ?? key, critere.key, { note: Number(e.target.value) })}
                        className={`opms__input opms__note-select${fieldErr?.note ? ' opms__input--error' : ''}`}
                        disabled={fieldDisabled}
                      >
                        {critere.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                    <span className="opms__note-out-of">/{critere.maxPoints}</span>
                  </div>
                </div>
              );
            })}

            <div className="opms__presta-footer">
              <div className="opms__note-globale">
                <h4>Note technique</h4>
                <div className="opms__note-value">
                  {hidden ? (
                    '-'
                  ) : iaEnCours ? (
                    <span className="opms-sk opms-sk-noteglobale" aria-hidden="true" />
                  ) : (
                    `${formatNoteGlobale(computeNoteGlobale(noteEntries))} / 10`
                  )}
                </div>
              </div>
            </div>
          </div>
        );
          })}
        </div>
      )}
    </>
  );
};

export default OperationAnalyseTechnique;
